/**
 * aw-toast - Toast Notification Component
 *
 * A container that manages toast notifications with event-based API.
 *
 * Types: success | error | warning | info | default
 *
 * Usage (declarative):
 *   <aw-toast></aw-toast>  <!-- Place once in layout -->
 *
 * Usage (programmatic):
 *   import { showToast } from './web-components';
 *   showToast({ message: 'Settings saved!', type: 'success' });
 *   showToast({ message: 'Deleted', type: 'info', undoCallback: () => restore() });
 */

export interface ToastOptions {
  message: string;
  type?: 'default' | 'info' | 'success' | 'warning' | 'error';
  duration?: number;
  undoCallback?: () => void;
}

interface ToastItem {
  id: number;
  message: string;
  type: string;
  undoCallback?: () => void;
  element?: HTMLElement;
}

export class AWToast extends HTMLElement {
  private toasts: ToastItem[] = [];
  private nextId = 0;
  private abortController = new AbortController();

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement('template');
    template.innerHTML = '<div class="container"></div>';

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        position: fixed;
        top: var(--space-6, 24px);
        right: var(--space-4, 16px);
        z-index: 10000;
        display: flex;
        flex-direction: column;
        gap: var(--space-3, 12px);
        pointer-events: none;
        max-width: 360px;
        width: calc(100vw - 32px);
      }

      @media (max-width: 480px) {
        :host {
          top: auto;
          bottom: var(--space-4, 16px);
          left: var(--space-4, 16px);
          right: var(--space-4, 16px);
          width: auto;
        }
      }

      .container {
        display: flex;
        flex-direction: column;
        gap: var(--space-3, 12px);
      }

      .toast {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3, 12px);
        padding: var(--space-3, 12px) var(--space-4, 16px);
        background: var(--bg-secondary, #151516);
        border: 1px solid var(--border-primary, #2a2a2c);
        border-radius: var(--radius-md, 6px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        pointer-events: auto;
        font-family: var(--font-family, inherit);
        font-size: var(--font-size-sm, 13px);
        line-height: 1.5;
        color: var(--text-primary, #eeeff1);
      }

      @media (prefers-reduced-motion: no-preference) {
        .toast {
          animation: slide-in 200ms ease-out;
        }

        .toast.removing {
          animation: slide-out 150ms ease-in forwards;
        }
      }

      @keyframes slide-in {
        from { opacity: 0; transform: translateX(100%); }
        to { opacity: 1; transform: translateX(0); }
      }

      @keyframes slide-out {
        from { opacity: 1; transform: translateX(0); }
        to { opacity: 0; transform: translateX(100%); }
      }

      @media (max-width: 480px) {
        @keyframes slide-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slide-out {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(20px); }
        }
      }

      /* Type variants */
      .toast.success {
        border-color: rgba(52, 199, 89, 0.3);
      }

      .toast.success .icon {
        color: var(--status-success, #34c759);
      }

      .toast.error {
        border-color: rgba(245, 69, 92, 0.3);
      }

      .toast.error .icon {
        color: var(--status-error, #f5455c);
      }

      .toast.warning {
        border-color: rgba(255, 193, 7, 0.3);
      }

      .toast.warning .icon {
        color: var(--status-warning, #ffc107);
      }

      .toast.info {
        border-color: rgba(94, 106, 210, 0.3);
      }

      .toast.info .icon {
        color: var(--accent-primary, #5e6ad2);
      }

      .icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .icon svg {
        width: 100%;
        height: 100%;
      }

      .message {
        flex: 1;
        min-width: 0;
        word-break: break-word;
      }

      .actions {
        display: flex;
        align-items: center;
        gap: var(--space-2, 8px);
        flex-shrink: 0;
        margin: -2px -4px -2px 0;
      }

      .undo-button,
      .close-button {
        background: transparent;
        border: none;
        color: inherit;
        cursor: pointer;
        padding: 4px;
        border-radius: var(--radius-sm, 4px);
        transition: background 150ms ease, color 150ms ease;
      }

      .undo-button {
        font-weight: 500;
        padding: 4px 8px;
        color: var(--accent-primary, #5e6ad2);
      }

      .undo-button:hover {
        background: rgba(94, 106, 210, 0.1);
      }

      .close-button {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-tertiary, #6b6f76);
      }

      .close-button:hover {
        color: var(--text-primary, #eeeff1);
        background: var(--bg-tertiary, #1a1a1b);
      }

      .close-button:focus-visible,
      .undo-button:focus-visible {
        outline: 2px solid var(--accent-primary, #5e6ad2);
        outline-offset: 2px;
      }

      .close-icon {
        width: 14px;
        height: 14px;
      }
    `;
  }

  private setupEventListeners(): void {
    const signal = this.abortController.signal;

    document.addEventListener('aw-toast', ((e: CustomEvent<ToastOptions>) => {
      this.show(e.detail);
    }) as EventListener, { signal });
  }

  show(options: ToastOptions): number {
    const id = this.nextId++;
    const type = options.type || 'default';
    const duration = options.duration ?? 4000;

    const toast: ToastItem = {
      id,
      message: options.message,
      type,
      undoCallback: options.undoCallback,
    };

    this.toasts.push(toast);
    this.renderToast(toast);

    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }

    return id;
  }

  private getIcon(type: string): string {
    const icons: Record<string, string> = {
      success: '<path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>',
      error: '<path d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>',
      warning: '<path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>',
      info: '<path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>',
    };
    return icons[type] || '';
  }

  private renderToast(toast: ToastItem): void {
    const container = this.shadowRoot?.querySelector('.container');
    if (!container) return;

    const el = document.createElement('div');
    el.className = `toast ${toast.type}`;
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-live', 'polite');
    el.dataset.toastId = String(toast.id);

    const iconSvg = this.getIcon(toast.type);
    if (iconSvg) {
      const iconEl = document.createElement('span');
      iconEl.className = 'icon';
      iconEl.innerHTML = `<svg viewBox="0 0 24 24">${iconSvg}</svg>`;
      el.appendChild(iconEl);
    }

    const messageEl = document.createElement('span');
    messageEl.className = 'message';
    messageEl.textContent = toast.message;
    el.appendChild(messageEl);

    const actionsEl = document.createElement('div');
    actionsEl.className = 'actions';

    if (toast.undoCallback) {
      const undoBtn = document.createElement('button');
      undoBtn.className = 'undo-button';
      undoBtn.type = 'button';
      undoBtn.textContent = 'Undo';
      undoBtn.addEventListener('click', () => {
        toast.undoCallback?.();
        this.dismiss(toast.id);
      });
      actionsEl.appendChild(undoBtn);
    }

    const closeBtn = document.createElement('button');
    closeBtn.className = 'close-button';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Dismiss');
    closeBtn.innerHTML = `
      <svg class="close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 6L6 18M6 6l12 12"/>
      </svg>
    `;
    closeBtn.addEventListener('click', () => this.dismiss(toast.id));
    actionsEl.appendChild(closeBtn);

    el.appendChild(actionsEl);
    container.appendChild(el);
    toast.element = el;
  }

  dismiss(id: number): void {
    const toast = this.toasts.find((t) => t.id === id);
    if (!toast?.element) return;

    toast.element.classList.add('removing');
    toast.element.addEventListener('animationend', () => {
      toast.element?.remove();
      this.toasts = this.toasts.filter((t) => t.id !== id);
    }, { once: true });

    // Fallback for reduced motion
    setTimeout(() => {
      if (toast.element?.parentNode) {
        toast.element.remove();
        this.toasts = this.toasts.filter((t) => t.id !== id);
      }
    }, 200);
  }

  dismissAll(): void {
    [...this.toasts].forEach((toast) => this.dismiss(toast.id));
  }
}

/**
 * Show a toast notification
 * @param options - Toast options (message, type, duration, undoCallback)
 */
export function showToast(options: ToastOptions): void {
  document.dispatchEvent(new CustomEvent('aw-toast', {
    detail: options,
    bubbles: true,
  }));
}

export default AWToast;
